import theme from "@shared/utils/themeColors";
import PropTypes from "prop-types";
import { useState } from "react";

/**
 * Simple pagination component that displays page numbers and navigation controls
 */
function PaginationNumeric({ currentPage = 1, totalPages = 1, onPageChange }) {
  // Internal state to track page changes
  const [internalPage, setInternalPage] = useState(currentPage);

  // Ensure we have valid numbers
  // const current = Math.max(1, parseInt(internalPage, 10));
  const total = Math.max(1, totalPages || 1);

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    // Validate page number
    if (newPage < 1 || newPage > total) {
      return;
    }

    // Update internal state first
    setInternalPage(newPage);
    onPageChange(newPage);
  };

  // Generate array of page numbers to display
  const generatePageNumbers = () => {
    // Simple case: 7 or fewer pages, show all of them
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // More complex case with ellipses
    let pages = [1]; // Always include first page

    // Calculate middle range
    let leftBound = Math.max(2, internalPage - 1);
    let rightBound = Math.min(total - 1, internalPage + 1);

    // Add middle pages
    for (let i = leftBound; i <= rightBound; i++) {
      pages.push(i);
    }

    // Always include last page if there is more than one page
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  // Generate the page numbers
  const pageNumbers = generatePageNumbers();

  // Check navigation button states
  const isPrevDisabled = internalPage <= 1;
  const isNextDisabled = internalPage >= total;

  return (
    <div className="flex justify-center">
      <nav className="flex" role="navigation" aria-label="Pagination">
        {/* Previous button */}
        <div className="mr-2">
          <button
            onClick={() => handlePageChange(internalPage - 1)}
            disabled={isPrevDisabled}
            className={`inline-flex items-center justify-center rounded-lg leading-5 px-2.5 py-2 border ${
              isPrevDisabled
                ? "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                : `bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 border-gray-200 dark:border-gray-700/60 ${theme.text.brand} shadow-sm cursor-pointer`
            }`}
          >
            <svg
              className="fill-current"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M9.4 13.4l1.4-1.4-4-4 4-4-1.4-1.4L4 8z" />
            </svg>
          </button>
        </div>

        {/* Page numbers */}
        <ul className="inline-flex text-sm font-medium -space-x-px rounded-lg shadow-sm">
          {pageNumbers.map((page, index) => {
            const isActive = page === internalPage;
            const isFirst = index === 0;
            const isLast = index === pageNumbers.length - 1;

            // Border radius classes
            let borderClass = "";
            if (pageNumbers.length === 1) {
              borderClass = "rounded-lg";
            } else if (isFirst) {
              borderClass = "rounded-l-lg";
            } else if (isLast) {
              borderClass = "rounded-r-lg";
            }

            // For active page
            if (isActive) {
              return (
                <li key={`page-${page}`}>
                  <span
                    className={`inline-flex items-center justify-center leading-5 px-3.5 py-2 ${theme.bg.primary} border ${theme.border.primary} ${theme.text.brand} ${borderClass}`}
                  >
                    {page}
                  </span>
                </li>
              );
            }

            // For clickable pages
            return (
              <li key={`page-${page}`}>
                <button
                  onClick={() => handlePageChange(page)}
                  className={`inline-flex items-center justify-center leading-5 px-3.5 py-2 ${theme.bg.primary} hover:bg-gray-50 dark:hover:bg-gray-900 border ${theme.border.primary} text-gray-600 dark:text-gray-300 cursor-pointer ${borderClass}`}
                >
                  {page}
                </button>
              </li>
            );
          })}
        </ul>

        {/* Next button */}
        <div className="ml-2">
          <button
            onClick={() => handlePageChange(internalPage + 1)}
            disabled={isNextDisabled}
            className={`inline-flex items-center justify-center rounded-lg leading-5 px-2.5 py-2 border ${
              isNextDisabled
                ? "${theme.bg.primary} ${theme.border.primary} text-gray-300 dark:text-gray-600 cursor-not-allowed"
                : "${theme.bg.primary} hover:bg-gray-50 dark:hover:bg-gray-900 ${theme.border.primary} ${theme.text.brand} shadow-sm cursor-pointer"
            }`}
          >
            <svg
              className="fill-current"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M6.6 13.4L5.2 12l4-4-4-4 1.4-1.4L12 8z" />
            </svg>
          </button>
        </div>
      </nav>
    </div>
  );
}

PaginationNumeric.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
};

export default PaginationNumeric;
